// Change #100503 - "i thought we could choose between staff address, company
// site location or customer as the start and end location". The old picker
// (Change #100454) was a dropdown that only listed what existed, so a company
// with no sites and a person with no addresses saw nothing but "Custom", and
// customers were never offered at all. Now the sources are always on show as
// buttons, in the same style as the job form's Job address, each one saying
// what to do when it has nothing to offer, with a line confirming where the
// shift starts / ends.
//
// The chosen address is COPIED onto the shift (onPick), never referenced, so
// editing a site or address later does not rewrite past shifts.

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import {
  getCustomerLookupAPI,
  getSingleCustomerSiteLocationListApiCall,
} from "../../../../helpers/fakebackend_helper";

const MODES = [
  { key: "staff", label: "Staff address", icon: "ri-home-4-line" },
  { key: "site", label: "Company site", icon: "ri-building-line" },
  { key: "customer", label: "Customer", icon: "ri-user-location-line" },
  { key: "other", label: "Another address", icon: "ri-edit-line" },
];

export const addressOneLine = (a) =>
  !a
    ? ""
    : a.AddressOneLine ||
      [a.AddressLine1, a.AddressLine2, a.AddressCity, a.AddressPostCode]
        .filter((x) => x && String(x).trim() !== "")
        .join(", ");

const customerName = (c) =>
  [c?.PersonFirstName, c?.PersonLastName].filter(Boolean).join(" ") || c?.FullName || `Customer #${c?.Id}`;

const customerSiteLabel = (s) => {
  const name = s?.BusinessName || s?.FullName || s?.PersonFullName || s?.SiteReference || `Site #${s?.Id}`;
  const where = addressOneLine(s);
  return where ? `${name} - ${where}` : name;
};

const ShiftLocationPicker = ({
  isEnd = false,
  address,
  onPick,
  companySites = [],
  staffAddresses = [],
  staffId,
  disabled = false,
  renderFields,
}) => {
  const current = addressOneLine(address);
  // A shift that already has an address opens on it, ready to edit; an empty
  // one opens on the person's own addresses, the usual start and end point.
  const [mode, setMode] = useState(current ? "other" : "staff");
  const [picked, setPicked] = useState({ staff: "", site: "", customer: "" });

  // ── customer lookup ────────────────────────────────────────────────────
  const [customerInput, setCustomerInput] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [customerSites, setCustomerSites] = useState([]);
  const [customerSitesLoading, setCustomerSitesLoading] = useState(false);
  const debounceRef = useRef(null);
  const lookupSeq = useRef(0);

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  const searchCustomers = (term) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const t = (term || "").trim();
    if (t.length < 2) {
      setCustomerOptions([]);
      setCustomerLoading(false);
      return;
    }
    setCustomerLoading(true);
    debounceRef.current = setTimeout(() => {
      const seq = ++lookupSeq.current;
      getCustomerLookupAPI(t, 25)
        .then((res) => {
          if (seq === lookupSeq.current) setCustomerOptions(res?.customer_list || []);
        })
        .catch(() => seq === lookupSeq.current && setCustomerOptions([]))
        .finally(() => seq === lookupSeq.current && setCustomerLoading(false));
    }, 300);
  };

  const chooseCustomer = (c) => {
    setCustomer(c || null);
    setCustomerSites([]);
    setPicked((p) => ({ ...p, customer: "" }));
    if (!c?.Id) return;
    // Their main address straight away when they have one; a site can still
    // be chosen from the list below once it loads.
    if (addressOneLine(c)) {
      onPick(c);
      setPicked((p) => ({ ...p, customer: "main" }));
    }
    setCustomerSitesLoading(true);
    getSingleCustomerSiteLocationListApiCall([{ customer_data: { Id: c.Id } }])
      .then((res) => setCustomerSites(res?.customersitelocation_list || []))
      .catch(() => setCustomerSites([]))
      .finally(() => setCustomerSitesLoading(false));
  };

  const pickFrom = (kind, value) => {
    setPicked((p) => ({ ...p, [kind]: value }));
    if (!value) return;
    let src = null;
    if (kind === "staff") src = staffAddresses.find((x) => String(x.Id) === String(value));
    if (kind === "site") src = companySites.find((x) => String(x.Id) === String(value));
    if (kind === "customer") {
      src = value === "main" ? customer : customerSites.find((x) => String(x.Id) === String(value));
    }
    if (src) onPick(src);
  };

  const emptyNote = (text, linkTo, linkText) => (
    <div className="alert alert-warning border py-2 px-3 mb-2 small">
      <i className="ri-information-line align-bottom me-1"></i>
      {text}{" "}
      {linkTo && (
        <Link to={linkTo} className="fw-semibold">
          {linkText}
        </Link>
      )}
    </div>
  );

  const nativeSelect = (kind, label, options) => (
    <TextField
      select
      fullWidth
      size="small"
      label={label}
      value={picked[kind]}
      onChange={(e) => pickFrom(kind, e.target.value)}
      disabled={disabled}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: true }}
      sx={{ mb: 1 }}
    >
      <option value="">Choose...</option>
      {options}
    </TextField>
  );

  return (
    <div>
      <label className="form-label mb-1">{isEnd ? "End location" : "Start location"}</label>
      <div className="btn-group w-100 mb-2 flex-wrap" role="group">
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            className={`btn btn-sm ${mode === m.key ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setMode(m.key)}
            disabled={disabled}
          >
            <i className={`${m.icon} align-bottom me-1`}></i>
            {m.label}
          </button>
        ))}
      </div>

      {mode === "staff" &&
        (staffAddresses.length === 0
          ? emptyNote(
              "This person has no addresses on file.",
              staffId ? `/staff-details/${staffId}/Addresses` : null,
              "Add one on the Addresses tab"
            )
          : nativeSelect(
              "staff",
              "Their address",
              staffAddresses.map((a) => (
                <option key={a.Id} value={a.Id}>
                  {a.StaffAddressType_Name || "Address"}
                  {addressOneLine(a) ? ` - ${addressOneLine(a)}` : ""}
                  {a.EndDate ? ` (until ${String(a.EndDate).slice(0, 10)})` : ""}
                </option>
              ))
            ))}

      {mode === "site" &&
        (companySites.length === 0
          ? emptyNote("No company site locations have been set up yet.", "/settings/1/51", "Add them in Settings")
          : nativeSelect(
              "site",
              "Company site",
              companySites.map((s) => (
                <option key={s.Id} value={s.Id}>
                  {s.Name}
                  {addressOneLine(s) ? ` - ${addressOneLine(s)}` : ""}
                </option>
              ))
            ))}

      {mode === "customer" && (
        <>
          <Autocomplete
            size="small"
            value={customer}
            inputValue={customerInput}
            options={customerOptions}
            loading={customerLoading}
            filterOptions={(opts) => opts}
            onInputChange={(_, val, reason) => {
              setCustomerInput(val);
              if (reason === "input") searchCustomers(val);
            }}
            onChange={(_, val) => chooseCustomer(val)}
            getOptionLabel={(c) => (c ? customerName(c) : "")}
            isOptionEqualToValue={(a, b) => String(a?.Id) === String(b?.Id)}
            renderOption={(props, c) => (
              <li {...props} key={c.Id}>
                <div>
                  <div className="fw-semibold">{customerName(c)}</div>
                  <small className="text-muted">{addressOneLine(c) || "No address on file"}</small>
                </div>
              </li>
            )}
            noOptionsText={customerInput.trim().length < 2 ? "Type 2 or more characters" : "No customers found"}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search customers by name, postcode, phone or email"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {customerLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 1 }}
          />
          {customer &&
            (customerSitesLoading
              ? <div className="small text-muted mb-2">Loading their sites...</div>
              : !addressOneLine(customer) && customerSites.length === 0
              ? emptyNote("This customer has no address or site locations on file.")
              : nativeSelect("customer", "Which address", [
                  addressOneLine(customer) ? (
                    <option key="main" value="main">
                      Main address - {addressOneLine(customer)}
                    </option>
                  ) : null,
                  ...customerSites.map((s) => (
                    <option key={s.Id} value={s.Id}>
                      {customerSiteLabel(s)}
                    </option>
                  )),
                ]))}
        </>
      )}

      {mode === "other" && (
        <div className="mb-2">
          {typeof renderFields === "function" && renderFields()}
        </div>
      )}

      {current ? (
        <div className="alert alert-light border py-2 px-3 mb-0 small">
          <i className="ri-map-pin-line align-bottom me-1"></i>
          This shift {isEnd ? "ends at" : "starts at"} <strong>{current}</strong>
          {mode !== "other" && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 ms-2 align-baseline"
              onClick={() => setMode("other")}
              disabled={disabled}
            >
              Edit
            </button>
          )}
        </div>
      ) : (
        <div className="alert alert-light border py-2 px-3 mb-0 small text-muted">
          <i className="ri-map-pin-line align-bottom me-1"></i>
          No {isEnd ? "end" : "start"} location set yet.
        </div>
      )}
    </div>
  );
};

export default ShiftLocationPicker;
